import type { AxiosInstance, AxiosRequestConfig } from 'axios';
import {
  useQuery,
  useMutation,
  useQueryClient,
  type QueryClient,
  type UseMutationOptions,
  type UseQueryOptions,
  type MutationFunction,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
export type UserRole = 'super_admin' | 'admin' | 'agent' | 'merchant';
export type UserStatus =
  'pending' | 'active' | 'blocked' | 'inactive' | 'rejected';
export type AvailabilityStatus = 'active' | 'paused';
export type AvailabilityStatusRequest = {
  status: AvailabilityStatus;
  reason?: string;
};
export type DealSource =
  'merchant_inbound' | 'agent_outbound' | 'admin_outbound';
export type DealStatus =
  | 'draft'
  | 'in_review'
  | 'agreed'
  | 'declined'
  | 'revoked'
  | 'expired'
  | 'terminated'
  | 'archived';
export type Problem = {
  code: string;
  message: string;
};
export type RegistrationRequest = {
  invite_token: string;
  email: string;
  password: string;
  first_name: string;
  last_name: string;
  company_name?: string;
  telegram?: string;
};
export type LoginRequest = {
  email: string;
  password: string;
};
export type RefreshRequest = {
  refresh_token: string;
};
export type ChangePasswordRequest = {
  old_password: string;
  new_password: string;
};
export type AuthResponse = {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
  user: User;
};
export type User = {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  company_name?: string;
  telegram?: string;
  role?: UserRole;
  status: UserStatus;
  invited_by_user_id?: string;
  created_at: string;
  updated_at: string;
};
export type RegistrationInvite = {
  id: string;
  created_by_user_id: string;
  used_by_user_id?: string;
  status: 'active' | 'used' | 'expired';
  max_uses: number;
  used_count: number;
  expires_at: string;
  last_used_at?: string;
  created_at: string;
  invite_token?: string;
  invite_url?: string;
};
export type RegistrationInviteList = {
  items: RegistrationInvite[];
  total: number;
  limit: number;
  offset: number;
};
export type MeResponse = {
  user: User;
  permissions: {
    [key: string]: boolean;
  };
};
export type ProfileUpdate = {
  first_name?: string;
  last_name?: string;
  company_name?: string;
  telegram?: string;
};
export type SetRoleRequest = {
  role: UserRole;
};
export type SetUserStatusRequest = {
  status: UserStatus;
  reason?: string;
};
export type UserList = {
  items: User[];
  total: number;
  limit: number;
  offset: number;
};
export type AuditEvent = {
  id: string;
  action: string;
  actor_user_id?: string;
  entity_type: string;
  entity_id: string;
  before?: {
    [key: string]: any;
  };
  after?: {
    [key: string]: any;
  };
  metadata: {
    [key: string]: any;
  };
  created_at: string;
};
export type AuditEventList = {
  items: AuditEvent[];
  total: number;
  limit: number;
  offset: number;
};
export type Dashboard = {
  deals: {
    [key: string]: number;
  };
  assigned_to_me: number;
};
export type ProviderInput = {
  real_name: string;
  alias?: string;
  description?: string;
  short_description?: string;
  full_description?: string;
  logo_url?: string;
  gallery_urls?: string[];
  countries?: string[];
  currencies?: string[];
  integration_methods?: string[];
  payment_methods?: string[];
  industries?: string[];
  jurisdictions?: string[];
  regulators?: string[];
  licenses?: ProviderLicense[];
  contacts?: ProviderContacts;
  support_contacts?: {
    [key: string]: any;
  };
  private_attributes?: {
    [key: string]: any;
  };
  status?: AvailabilityStatus;
};
export type Provider = ProviderInput & {
  id: string;
  is_deleted: boolean;
  deleted_at?: string;
  deleted_by?: string;
  offers_count: number;
  created_by: string;
  created_at: string;
  updated_at: string;
};
export type ProviderList = {
  items: Provider[];
  total: number;
  limit: number;
  offset: number;
};
export type ProviderLicense = {
  name: string;
  document_url?: string;
};
export type ProviderContacts = {
  contact_name?: string;
  email?: string;
  phone?: string;
  website?: string;
  linkedin?: string;
  social_links?: string[];
  messengers?: string[];
};
export type FileUploadPurpose =
  'provider_logo' | 'provider_gallery' | 'provider_document';
export type FileUploadRequest = {
  purpose: FileUploadPurpose;
  files: string[];
};
export type FileLinks = string[];
export type ProviderOption = {
  code: string;
  name: string;
  flag?: string;
};
export type ProviderOptions = {
  countries: ProviderOption[];
  currencies: ProviderOption[];
  integration_methods: ProviderOption[];
  payment_methods: ProviderOption[];
  industries: ProviderOption[];
};
export type ProviderAvailabilityImpact = {
  provider_id: string;
  provider_status: AvailabilityStatus;
  offers: Offer[];
  active_deals: Deal[];
  affected_offers_count: number;
  affected_deals_count: number;
};
export type Amount = string;
export type TrafficType =
  'trusted' | 'ftd' | 'nftd' | 'incentive' | 'grey' | 'quasi_ecom';
export type Fee = {
  value: Amount;
  unit: 'percent' | 'fixed';
};
export type AmountLimits = {
  min?: Amount;
  max?: Amount;
};
export type RollingReserve = {
  percent?: Amount;
  hold_days?: number;
  cap_limit?: Amount;
};
export type LimitsAndFees = {
  guarantee_amount?: Amount;
  refund?: Amount;
  chargeback?: Amount;
  rolling_reserve?: RollingReserve;
};
export type CustomField = {
  name: string;
  value: string;
};
export type OfferAdditional = {
  requirements?: string;
  flow?: string;
  notes?: string;
  custom_fields?: CustomField[];
};
export type PaymentLegInput = {
  provider_rate: Fee;
  sell_rate: Fee;
  methods?: string[];
  min_fee?: Fee;
  kyc_limits?: string;
  limits?: AmountLimits;
};
export type PaymentLeg = {
  provider_rate?: Fee;
  sell_rate: Fee;
  margin?: string;
  agent_margin?: Amount;
  final_rate?: Fee;
  methods?: string[];
  min_fee?: Fee;
  kyc_limits?: string;
  limits?: AmountLimits;
};
export type SettlementLegInput = {
  provider_rate: Fee;
  sell_rate: Fee;
  terms?: string;
  provider?: string;
  asset?: string;
  min_amount?: Amount;
  exchange_rate: string;
  limits?: AmountLimits;
};
export type SettlementLeg = {
  provider_rate?: Fee;
  sell_rate: Fee;
  margin?: string;
  agent_margin?: Amount;
  final_rate?: Fee;
  terms?: string;
  provider?: string;
  asset?: string;
  min_amount?: Amount;
  exchange_rate: string;
  limits?: AmountLimits;
};
export type ProviderOfferCapabilities = {
  provider_id: string;
  countries: string[];
  currencies: string[];
  integration_methods: string[];
  payment_methods: string[];
};
export type OfferInput = {
  provider_id: string;
  name?: string;
  alias: string;
  countries?: string[];
  currencies: string[];
  traffic_types: TrafficType[];
  integration_methods: string[];
  pay_in?: PaymentLegInput;
  pay_out?: PaymentLegInput;
  settlement?: SettlementLegInput;
  crypto_top_up?: SettlementLegInput;
  limits_and_fees?: LimitsAndFees;
  additional?: OfferAdditional;
  status?: AvailabilityStatus;
};
export type Offer = {
  id: string;
  provider_id?: string;
  name: string;
  alias: string;
  countries: string[];
  currencies: string[];
  traffic_types: TrafficType[];
  integration_methods: string[];
  pay_in?: PaymentLeg;
  pay_out?: PaymentLeg;
  settlement?: SettlementLeg;
  crypto_top_up?: SettlementLeg;
  limits_and_fees?: LimitsAndFees;
  additional?: OfferAdditional;
  status: AvailabilityStatus;
  is_deleted: boolean;
  deleted_at?: string;
  deleted_by?: string;
  created_by?: string;
  created_at: string;
  updated_at: string;
};
export type OfferList = {
  items: Offer[];
  total: number;
  limit: number;
  offset: number;
};
export type OfferDuplicateDraft = {
  source_offer_id: string;
  provider_id: string;
  name: string;
  alias: string;
  countries: string[];
  currencies: string[];
  traffic_types: TrafficType[];
  integration_methods: string[];
  pay_in?: PaymentLeg;
  pay_out?: PaymentLeg;
  settlement?: SettlementLeg;
  crypto_top_up?: SettlementLeg;
  limits_and_fees?: LimitsAndFees;
  additional?: OfferAdditional;
};
export type OfferBulkStatusRequest = {
  offer_ids: string[];
  status: AvailabilityStatus;
};
export type CatalogFacets = {
  statuses: AvailabilityStatus[];
  provider_ids: string[];
  countries: string[];
  currencies: string[];
  traffic_types: TrafficType[];
  integration_methods: string[];
};
export type DealItemInput = {
  offer_id: string;
  agent_margins?: {
    [key: string]: Amount;
  };
};
export type CreateDeal = {
  source: DealSource;
  merchant_id?: string;
  merchant_brand?: string;
  merchant_country: string;
  merchant_currency: string;
  comment?: string;
  items?: DealItemInput[];
};
export type DealItem = {
  id: string;
  offer_id: string;
  provider_id?: string;
  offer_alias: string;
  offer: Offer;
  selected: boolean;
  created_at: string;
  updated_at: string;
};
export type Deal = {
  id: string;
  merchant_id?: string;
  agent_id?: string;
  assignee_id?: string;
  source: DealSource;
  status: DealStatus;
  merchant_brand?: string;
  merchant_country: string;
  merchant_currency: string;
  initial_comment?: string;
  items: DealItem[];
  created_at: string;
  updated_at: string;
};
export type DealResponse = {
  deal: Deal;
  selected_provider?: Provider;
};
export type DealList = {
  items: Deal[];
  total: number;
  limit: number;
  offset: number;
};
export type MarginRequest = {
  agent_margins: {
    [key: string]: Amount;
  };
};
export type DealLink = {
  id: string;
  url: string;
  expires_at: string;
  deal_status: DealStatus;
};
export type DealEvent = {
  id: string;
  deal_id: string;
  actor_id?: string;
  event_type: string;
  payload: {
    [key: string]: any;
  };
  comment?: string;
  field_reference?: string;
  created_at: string;
};
export type DealEventList = {
  items: DealEvent[];
  limit: number;
  offset: number;
};
export type ReasonRequest = {
  reason?: string;
};
export type RateChange = {
  deal_item_id: string;
  rate_key: 'pay_in' | 'pay_out' | 'settlement' | 'crypto_top_up';
  requested_base?: string;
  platform_margin?: string;
};
export type SpecialTermsInput = {
  comment: string;
  requested_changes: RateChange[];
};
export type SpecialTermsDecision = {
  comment?: string;
  changes?: RateChange[];
};
export type SpecialTermsRequest = {
  id: string;
  deal_id: string;
  requested_by: string;
  resolved_by?: string;
  status: 'pending' | 'approved' | 'rejected';
  requested_changes: RateChange[];
  resolved_changes?: RateChange[];
  comment: string;
  resolution_comment?: string;
  created_at: string;
  resolved_at?: string;
};
export type Contacts = {
  [key: string]: any;
};
export type AcceptProposal = {
  deal_item_id: string;
  contacts?: Contacts;
};
export type AcceptProposalResponse = {
  deal_id: string;
  status: DealStatus;
  accepted_at: string;
};
export type Funnel = {
  submitted: number;
  in_review: number;
  accepted: number;
  declined: number;
  revoked: number;
  expired: number;
  special_terms_requested: number;
  conversion: string;
};
export type TrackingEvent = {
  event: 'catalog_filter_applied' | 'offer_added_to_favorites';
  properties?: {
    [key: string]: any;
  };
};
export type AxiosConfig = {
  paramsSerializer?: AxiosRequestConfig['paramsSerializer'];
};
export type Config = {
  mutations?: MutationConfigs;
  axios?: AxiosConfig;
};
export function initialize(axios: AxiosInstance, config?: Config) {
  const requests = makeRequests(axios, config?.axios);
  return {
    requests,
    queries: makeQueries(requests),
    mutations: makeMutations(requests, config?.mutations),
  };
}
function useRapiniMutation<
  TData = unknown,
  TError = unknown,
  TVariables = void,
  TContext = unknown,
>(
  mutationFn: MutationFunction<TData, TVariables>,
  config?: (
    queryClient: QueryClient,
  ) => Pick<
    UseMutationOptions<TData, TError, TVariables, TContext>,
    'onSuccess' | 'onSettled' | 'onError'
  >,
  options?: Omit<
    UseMutationOptions<TData, TError, TVariables, TContext>,
    'mutationFn'
  >,
): UseMutationResult<TData, TError, TVariables, TContext> {
  const { onSuccess, onError, onSettled, ...rest } = options ?? {};
  const queryClient = useQueryClient();
  const conf = config?.(queryClient);
  const mutationOptions: typeof options = {
    onSuccess: (data: TData, variables: TVariables, context: TContext) => {
      conf?.onSuccess?.(data, variables, context);
      onSuccess?.(data, variables, context);
    },
    onError: (error: TError, variables: TVariables, context?: TContext) => {
      conf?.onError?.(error, variables, context);
      onError?.(error, variables, context);
    },
    onSettled: (
      data: TData | undefined,
      error: TError | null,
      variables: TVariables,
      context?: TContext,
    ) => {
      conf?.onSettled?.(data, error, variables, context);
      onSettled?.(data, error, variables, context);
    },
    ...rest,
  };
  return useMutation({ mutationFn, ...mutationOptions });
}
function nullIfUndefined<T>(value: T): NonNullable<T> | null {
  return typeof value === 'undefined' ? null : (value as NonNullable<T> | null);
}
export const queryKeys = {
  getMe: () => ['getMe'] as const,
  getMyDashboard: () => ['getMyDashboard'] as const,
  listUsers: (
    page?: number,
    limit?: number,
    status?: UserStatus,
    role?: UserRole,
    search?: string,
  ) =>
    [
      'listUsers',
      nullIfUndefined(page),
      nullIfUndefined(limit),
      nullIfUndefined(status),
      nullIfUndefined(role),
      nullIfUndefined(search),
    ] as const,
  listRegistrationInvites: (page?: number, limit?: number) =>
    [
      'listRegistrationInvites',
      nullIfUndefined(page),
      nullIfUndefined(limit),
    ] as const,
  getUser: (userID: string) => ['getUser', userID] as const,
  listAuditEvents: (
    page?: number,
    limit?: number,
    actor_user_id?: string,
    action?: string,
    entity_type?: string,
    entity_id?: string,
    from?: string,
    to?: string,
  ) =>
    [
      'listAuditEvents',
      nullIfUndefined(page),
      nullIfUndefined(limit),
      nullIfUndefined(actor_user_id),
      nullIfUndefined(action),
      nullIfUndefined(entity_type),
      nullIfUndefined(entity_id),
      nullIfUndefined(from),
      nullIfUndefined(to),
    ] as const,
  getAuditEvent: (eventID: string) => ['getAuditEvent', eventID] as const,
  getCatalogFacets: (
    statuses?: AvailabilityStatus[],
    provider_ids?: string[],
    countries?: string[],
    currencies?: string[],
    traffic_types?: TrafficType[],
    integration_methods?: string[],
  ) =>
    [
      'getCatalogFacets',
      nullIfUndefined(statuses),
      nullIfUndefined(provider_ids),
      nullIfUndefined(countries),
      nullIfUndefined(currencies),
      nullIfUndefined(traffic_types),
      nullIfUndefined(integration_methods),
    ] as const,
  listCatalogOffers: (
    page?: number,
    limit?: number,
    search?: string,
    sort?: 'newest' | 'oldest',
    statuses?: AvailabilityStatus[],
    provider_ids?: string[],
    countries?: string[],
    currencies?: string[],
    traffic_types?: TrafficType[],
    integration_methods?: string[],
  ) =>
    [
      'listCatalogOffers',
      nullIfUndefined(page),
      nullIfUndefined(limit),
      nullIfUndefined(search),
      nullIfUndefined(sort),
      nullIfUndefined(statuses),
      nullIfUndefined(provider_ids),
      nullIfUndefined(countries),
      nullIfUndefined(currencies),
      nullIfUndefined(traffic_types),
      nullIfUndefined(integration_methods),
    ] as const,
  getCatalogOffer: (offerID: string) => ['getCatalogOffer', offerID] as const,
  listProviders: (
    page?: number,
    limit?: number,
    status?: AvailabilityStatus,
    search?: string,
    country?: string[],
    currency?: string[],
    integration_method?: string[],
    payment_method?: string[],
    industry?: string[],
    is_deleted?: boolean,
  ) =>
    [
      'listProviders',
      nullIfUndefined(page),
      nullIfUndefined(limit),
      nullIfUndefined(status),
      nullIfUndefined(search),
      nullIfUndefined(country),
      nullIfUndefined(currency),
      nullIfUndefined(integration_method),
      nullIfUndefined(payment_method),
      nullIfUndefined(industry),
      nullIfUndefined(is_deleted),
    ] as const,
  getProviderOptions: () => ['getProviderOptions'] as const,
  getProvider: (providerID: string) => ['getProvider', providerID] as const,
  getProviderOfferCapabilities: (providerID: string) =>
    ['getProviderOfferCapabilities', providerID] as const,
  getProviderAvailabilityImpact: (providerID: string) =>
    ['getProviderAvailabilityImpact', providerID] as const,
  listAdminOffers: (
    page?: number,
    limit?: number,
    search?: string,
    sort?: 'newest' | 'oldest',
    statuses?: AvailabilityStatus[],
    provider_ids?: string[],
    countries?: string[],
    currencies?: string[],
    traffic_types?: TrafficType[],
    integration_methods?: string[],
    is_deleted?: boolean,
  ) =>
    [
      'listAdminOffers',
      nullIfUndefined(page),
      nullIfUndefined(limit),
      nullIfUndefined(search),
      nullIfUndefined(sort),
      nullIfUndefined(statuses),
      nullIfUndefined(provider_ids),
      nullIfUndefined(countries),
      nullIfUndefined(currencies),
      nullIfUndefined(traffic_types),
      nullIfUndefined(integration_methods),
      nullIfUndefined(is_deleted),
    ] as const,
  getAdminOffer: (offerID: string) => ['getAdminOffer', offerID] as const,
  getOfferDuplicateDraft: (offerID: string) =>
    ['getOfferDuplicateDraft', offerID] as const,
  listDeals: (
    page?: number,
    limit?: number,
    status?: DealStatus,
    source?: DealSource,
  ) =>
    [
      'listDeals',
      nullIfUndefined(page),
      nullIfUndefined(limit),
      nullIfUndefined(status),
      nullIfUndefined(source),
    ] as const,
  getDeal: (dealID: string) => ['getDeal', dealID] as const,
  getDealEvents: (dealID: string, page?: number, limit?: number) =>
    [
      'getDealEvents',
      dealID,
      nullIfUndefined(page),
      nullIfUndefined(limit),
    ] as const,
  getPublicProposal: (token: string) => ['getPublicProposal', token] as const,
  getAnalyticsFunnel: (from?: string, to?: string) =>
    ['getAnalyticsFunnel', nullIfUndefined(from), nullIfUndefined(to)] as const,
} as const;
export type QueryKeys = typeof queryKeys;
function makeRequests(axios: AxiosInstance, config?: AxiosConfig) {
  return {
    register: (payload: RegistrationRequest) =>
      axios
        .request<User>({
          method: 'post',
          url: `/auth/register`,
          data: payload,
          headers: {
            'Content-Type': 'application/json',
          },
        })
        .then((res) => res.data),
    login: (payload: LoginRequest) =>
      axios
        .request<AuthResponse>({
          method: 'post',
          url: `/auth/login`,
          data: payload,
          headers: {
            'Content-Type': 'application/json',
          },
        })
        .then((res) => res.data),
    refreshTokens: (payload: RefreshRequest) =>
      axios
        .request<AuthResponse>({
          method: 'post',
          url: `/auth/refresh`,
          data: payload,
          headers: {
            'Content-Type': 'application/json',
          },
        })
        .then((res) => res.data),
    logout: (payload: RefreshRequest) =>
      axios
        .request<Problem>({
          method: 'post',
          url: `/auth/logout`,
          data: payload,
          headers: {
            'Content-Type': 'application/json',
          },
        })
        .then((res) => res.data),
    changePassword: (payload: ChangePasswordRequest) =>
      axios
        .request<Problem>({
          method: 'post',
          url: `/auth/change-password`,
          data: payload,
          headers: {
            'Content-Type': 'application/json',
          },
        })
        .then((res) => res.data),
    getMe: () =>
      axios
        .request<MeResponse>({
          method: 'get',
          url: `/me`,
        })
        .then((res) => res.data),
    updateMe: (payload: ProfileUpdate) =>
      axios
        .request<MeResponse>({
          method: 'patch',
          url: `/me`,
          data: payload,
          headers: {
            'Content-Type': 'application/json',
          },
        })
        .then((res) => res.data),
    getMyDashboard: () =>
      axios
        .request<Dashboard>({
          method: 'get',
          url: `/me/dashboard`,
        })
        .then((res) => res.data),
    listUsers: (
      page?: number,
      limit?: number,
      status?: UserStatus,
      role?: UserRole,
      search?: string,
    ) =>
      axios
        .request<UserList>({
          method: 'get',
          url: `/users`,
          params: {
            ...(page !== undefined ? { page } : undefined),
            ...(limit !== undefined ? { limit } : undefined),
            ...(status !== undefined ? { status } : undefined),
            ...(role !== undefined ? { role } : undefined),
            ...(search !== undefined ? { search } : undefined),
          },
          paramsSerializer: config?.paramsSerializer,
        })
        .then((res) => res.data),
    listRegistrationInvites: (page?: number, limit?: number) =>
      axios
        .request<RegistrationInviteList>({
          method: 'get',
          url: `/users/invites`,
          params: {
            ...(page !== undefined ? { page } : undefined),
            ...(limit !== undefined ? { limit } : undefined),
          },
          paramsSerializer: config?.paramsSerializer,
        })
        .then((res) => res.data),
    createRegistrationInvite: () =>
      axios
        .request<RegistrationInvite>({
          method: 'post',
          url: `/users/invites`,
        })
        .then((res) => res.data),
    getUser: (userID: string) =>
      axios
        .request<User>({
          method: 'get',
          url: `/users/${userID}`,
        })
        .then((res) => res.data),
    setUserRole: (payload: SetRoleRequest, userID: string) =>
      axios
        .request<User>({
          method: 'patch',
          url: `/users/${userID}/role`,
          data: payload,
          headers: {
            'Content-Type': 'application/json',
          },
        })
        .then((res) => res.data),
    setUserStatus: (payload: SetUserStatusRequest, userID: string) =>
      axios
        .request<User>({
          method: 'patch',
          url: `/users/${userID}/status`,
          data: payload,
          headers: {
            'Content-Type': 'application/json',
          },
        })
        .then((res) => res.data),
    listAuditEvents: (
      page?: number,
      limit?: number,
      actor_user_id?: string,
      action?: string,
      entity_type?: string,
      entity_id?: string,
      from?: string,
      to?: string,
    ) =>
      axios
        .request<AuditEventList>({
          method: 'get',
          url: `/audit/events`,
          params: {
            ...(page !== undefined ? { page } : undefined),
            ...(limit !== undefined ? { limit } : undefined),
            ...(actor_user_id !== undefined ? { actor_user_id } : undefined),
            ...(action !== undefined ? { action } : undefined),
            ...(entity_type !== undefined ? { entity_type } : undefined),
            ...(entity_id !== undefined ? { entity_id } : undefined),
            ...(from !== undefined ? { from } : undefined),
            ...(to !== undefined ? { to } : undefined),
          },
          paramsSerializer: config?.paramsSerializer,
        })
        .then((res) => res.data),
    getAuditEvent: (eventID: string) =>
      axios
        .request<AuditEvent>({
          method: 'get',
          url: `/audit/events/${eventID}`,
        })
        .then((res) => res.data),
    getCatalogFacets: (
      statuses?: AvailabilityStatus[],
      provider_ids?: string[],
      countries?: string[],
      currencies?: string[],
      traffic_types?: TrafficType[],
      integration_methods?: string[],
    ) =>
      axios
        .request<CatalogFacets>({
          method: 'get',
          url: `/catalog/facets`,
          params: {
            ...(statuses !== undefined ? { statuses } : undefined),
            ...(provider_ids !== undefined ? { provider_ids } : undefined),
            ...(countries !== undefined ? { countries } : undefined),
            ...(currencies !== undefined ? { currencies } : undefined),
            ...(traffic_types !== undefined ? { traffic_types } : undefined),
            ...(integration_methods !== undefined
              ? { integration_methods }
              : undefined),
          },
          paramsSerializer: config?.paramsSerializer,
        })
        .then((res) => res.data),
    listCatalogOffers: (
      page?: number,
      limit?: number,
      search?: string,
      sort?: 'newest' | 'oldest',
      statuses?: AvailabilityStatus[],
      provider_ids?: string[],
      countries?: string[],
      currencies?: string[],
      traffic_types?: TrafficType[],
      integration_methods?: string[],
    ) =>
      axios
        .request<OfferList>({
          method: 'get',
          url: `/catalog/offers`,
          params: {
            ...(page !== undefined ? { page } : undefined),
            ...(limit !== undefined ? { limit } : undefined),
            ...(search !== undefined ? { search } : undefined),
            ...(sort !== undefined ? { sort } : undefined),
            ...(statuses !== undefined ? { statuses } : undefined),
            ...(provider_ids !== undefined ? { provider_ids } : undefined),
            ...(countries !== undefined ? { countries } : undefined),
            ...(currencies !== undefined ? { currencies } : undefined),
            ...(traffic_types !== undefined ? { traffic_types } : undefined),
            ...(integration_methods !== undefined
              ? { integration_methods }
              : undefined),
          },
          paramsSerializer: config?.paramsSerializer,
        })
        .then((res) => res.data),
    getCatalogOffer: (offerID: string) =>
      axios
        .request<Offer>({
          method: 'get',
          url: `/catalog/offers/${offerID}`,
        })
        .then((res) => res.data),
    uploadProviderFiles: (payload: FileUploadRequest) =>
      axios
        .request<FileLinks>({
          method: 'post',
          url: `/admin/files`,
          data: payload,
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        })
        .then((res) => res.data),
    listProviders: (
      page?: number,
      limit?: number,
      status?: AvailabilityStatus,
      search?: string,
      country?: string[],
      currency?: string[],
      integration_method?: string[],
      payment_method?: string[],
      industry?: string[],
      is_deleted?: boolean,
    ) =>
      axios
        .request<ProviderList>({
          method: 'get',
          url: `/admin/providers`,
          params: {
            ...(page !== undefined ? { page } : undefined),
            ...(limit !== undefined ? { limit } : undefined),
            ...(status !== undefined ? { status } : undefined),
            ...(search !== undefined ? { search } : undefined),
            ...(country !== undefined ? { country } : undefined),
            ...(currency !== undefined ? { currency } : undefined),
            ...(integration_method !== undefined
              ? { integration_method }
              : undefined),
            ...(payment_method !== undefined ? { payment_method } : undefined),
            ...(industry !== undefined ? { industry } : undefined),
            ...(is_deleted !== undefined ? { is_deleted } : undefined),
          },
          paramsSerializer: config?.paramsSerializer,
        })
        .then((res) => res.data),
    createProvider: (payload: ProviderInput) =>
      axios
        .request<Provider>({
          method: 'post',
          url: `/admin/providers`,
          data: payload,
          headers: {
            'Content-Type': 'application/json',
          },
        })
        .then((res) => res.data),
    getProviderOptions: () =>
      axios
        .request<ProviderOptions>({
          method: 'get',
          url: `/admin/providers/options`,
        })
        .then((res) => res.data),
    getProvider: (providerID: string) =>
      axios
        .request<Provider>({
          method: 'get',
          url: `/admin/providers/${providerID}`,
        })
        .then((res) => res.data),
    deleteProvider: (providerID: string) =>
      axios
        .request<Problem>({
          method: 'delete',
          url: `/admin/providers/${providerID}`,
        })
        .then((res) => res.data),
    updateProvider: (payload: ProviderInput, providerID: string) =>
      axios
        .request<Provider>({
          method: 'patch',
          url: `/admin/providers/${providerID}`,
          data: payload,
          headers: {
            'Content-Type': 'application/json',
          },
        })
        .then((res) => res.data),
    restoreProvider: (providerID: string) =>
      axios
        .request<Provider>({
          method: 'post',
          url: `/admin/providers/${providerID}/restore`,
        })
        .then((res) => res.data),
    getProviderOfferCapabilities: (providerID: string) =>
      axios
        .request<ProviderOfferCapabilities>({
          method: 'get',
          url: `/admin/providers/${providerID}/offer-capabilities`,
        })
        .then((res) => res.data),
    setProviderStatus: (
      payload: AvailabilityStatusRequest,
      providerID: string,
    ) =>
      axios
        .request<Provider>({
          method: 'patch',
          url: `/admin/providers/${providerID}/status`,
          data: payload,
          headers: {
            'Content-Type': 'application/json',
          },
        })
        .then((res) => res.data),
    getProviderAvailabilityImpact: (providerID: string) =>
      axios
        .request<ProviderAvailabilityImpact>({
          method: 'get',
          url: `/admin/providers/${providerID}/availability-impact`,
        })
        .then((res) => res.data),
    listAdminOffers: (
      page?: number,
      limit?: number,
      search?: string,
      sort?: 'newest' | 'oldest',
      statuses?: AvailabilityStatus[],
      provider_ids?: string[],
      countries?: string[],
      currencies?: string[],
      traffic_types?: TrafficType[],
      integration_methods?: string[],
      is_deleted?: boolean,
    ) =>
      axios
        .request<OfferList>({
          method: 'get',
          url: `/admin/offers`,
          params: {
            ...(page !== undefined ? { page } : undefined),
            ...(limit !== undefined ? { limit } : undefined),
            ...(search !== undefined ? { search } : undefined),
            ...(sort !== undefined ? { sort } : undefined),
            ...(statuses !== undefined ? { statuses } : undefined),
            ...(provider_ids !== undefined ? { provider_ids } : undefined),
            ...(countries !== undefined ? { countries } : undefined),
            ...(currencies !== undefined ? { currencies } : undefined),
            ...(traffic_types !== undefined ? { traffic_types } : undefined),
            ...(integration_methods !== undefined
              ? { integration_methods }
              : undefined),
            ...(is_deleted !== undefined ? { is_deleted } : undefined),
          },
          paramsSerializer: config?.paramsSerializer,
        })
        .then((res) => res.data),
    createOffer: (payload: OfferInput) =>
      axios
        .request<Offer>({
          method: 'post',
          url: `/admin/offers`,
          data: payload,
          headers: {
            'Content-Type': 'application/json',
          },
        })
        .then((res) => res.data),
    getAdminOffer: (offerID: string) =>
      axios
        .request<Offer>({
          method: 'get',
          url: `/admin/offers/${offerID}`,
        })
        .then((res) => res.data),
    deleteOffer: (offerID: string) =>
      axios
        .request<Problem>({
          method: 'delete',
          url: `/admin/offers/${offerID}`,
        })
        .then((res) => res.data),
    updateOffer: (payload: OfferInput, offerID: string) =>
      axios
        .request<Offer>({
          method: 'patch',
          url: `/admin/offers/${offerID}`,
          data: payload,
          headers: {
            'Content-Type': 'application/json',
          },
        })
        .then((res) => res.data),
    restoreOffer: (offerID: string) =>
      axios
        .request<Offer>({
          method: 'post',
          url: `/admin/offers/${offerID}/restore`,
        })
        .then((res) => res.data),
    getOfferDuplicateDraft: (offerID: string) =>
      axios
        .request<OfferDuplicateDraft>({
          method: 'get',
          url: `/admin/offers/${offerID}/duplicate`,
        })
        .then((res) => res.data),
    setOfferStatus: (payload: AvailabilityStatusRequest, offerID: string) =>
      axios
        .request<Offer>({
          method: 'patch',
          url: `/admin/offers/${offerID}/status`,
          data: payload,
          headers: {
            'Content-Type': 'application/json',
          },
        })
        .then((res) => res.data),
    bulkSetOfferStatus: (payload: OfferBulkStatusRequest) =>
      axios
        .request<OfferList>({
          method: 'patch',
          url: `/admin/offers/bulk-status`,
          data: payload,
          headers: {
            'Content-Type': 'application/json',
          },
        })
        .then((res) => res.data),
    listDeals: (
      page?: number,
      limit?: number,
      status?: DealStatus,
      source?: DealSource,
    ) =>
      axios
        .request<DealList>({
          method: 'get',
          url: `/deals`,
          params: {
            ...(page !== undefined ? { page } : undefined),
            ...(limit !== undefined ? { limit } : undefined),
            ...(status !== undefined ? { status } : undefined),
            ...(source !== undefined ? { source } : undefined),
          },
          paramsSerializer: config?.paramsSerializer,
        })
        .then((res) => res.data),
    createDeal: (payload: CreateDeal) =>
      axios
        .request<Deal>({
          method: 'post',
          url: `/deals`,
          data: payload,
          headers: {
            'Content-Type': 'application/json',
          },
        })
        .then((res) => res.data),
    getDeal: (dealID: string) =>
      axios
        .request<DealResponse>({
          method: 'get',
          url: `/deals/${dealID}`,
        })
        .then((res) => res.data),
    updateDraftDeal: (payload: CreateDeal, dealID: string) =>
      axios
        .request<Deal>({
          method: 'patch',
          url: `/deals/${dealID}`,
          data: payload,
          headers: {
            'Content-Type': 'application/json',
          },
        })
        .then((res) => res.data),
    addDealItem: (payload: DealItemInput, dealID: string) =>
      axios
        .request<Deal>({
          method: 'post',
          url: `/deals/${dealID}/items`,
          data: payload,
          headers: {
            'Content-Type': 'application/json',
          },
        })
        .then((res) => res.data),
    deleteDealItem: (dealID: string, itemID: string) =>
      axios
        .request<Problem>({
          method: 'delete',
          url: `/deals/${dealID}/items/${itemID}`,
        })
        .then((res) => res.data),
    updateDealItemMargin: (
      payload: MarginRequest,
      dealID: string,
      itemID: string,
    ) =>
      axios
        .request<DealItem>({
          method: 'patch',
          url: `/deals/${dealID}/items/${itemID}`,
          data: payload,
          headers: {
            'Content-Type': 'application/json',
          },
        })
        .then((res) => res.data),
    generateDealLink: (dealID: string) =>
      axios
        .request<DealLink>({
          method: 'post',
          url: `/deals/${dealID}/links`,
        })
        .then((res) => res.data),
    getDealEvents: (dealID: string, page?: number, limit?: number) =>
      axios
        .request<DealEventList>({
          method: 'get',
          url: `/deals/${dealID}/events`,
          params: {
            ...(page !== undefined ? { page } : undefined),
            ...(limit !== undefined ? { limit } : undefined),
          },
          paramsSerializer: config?.paramsSerializer,
        })
        .then((res) => res.data),
    revokeDeal: (payload: ReasonRequest, dealID: string) =>
      axios
        .request<Deal>({
          method: 'post',
          url: `/deals/${dealID}/revoke`,
          data: payload,
          headers: {
            'Content-Type': 'application/json',
          },
        })
        .then((res) => res.data),
    terminateDeal: (payload: ReasonRequest, dealID: string) =>
      axios
        .request<Deal>({
          method: 'post',
          url: `/deals/${dealID}/terminate`,
          data: payload,
          headers: {
            'Content-Type': 'application/json',
          },
        })
        .then((res) => res.data),
    archiveDeal: (payload: ReasonRequest, dealID: string) =>
      axios
        .request<Deal>({
          method: 'post',
          url: `/deals/${dealID}/archive`,
          data: payload,
          headers: {
            'Content-Type': 'application/json',
          },
        })
        .then((res) => res.data),
    requestSpecialTerms: (payload: SpecialTermsInput, dealID: string) =>
      axios
        .request<SpecialTermsRequest>({
          method: 'post',
          url: `/deals/${dealID}/special-terms-requests`,
          data: payload,
          headers: {
            'Content-Type': 'application/json',
          },
        })
        .then((res) => res.data),
    resolveSpecialTerms: (
      payload: SpecialTermsDecision,
      dealID: string,
      requestID: string,
    ) =>
      axios
        .request<SpecialTermsRequest>({
          method: 'post',
          url: `/deals/${dealID}/special-terms-requests/${requestID}/resolve`,
          data: payload,
          headers: {
            'Content-Type': 'application/json',
          },
        })
        .then((res) => res.data),
    rejectSpecialTerms: (
      payload: SpecialTermsDecision,
      dealID: string,
      requestID: string,
    ) =>
      axios
        .request<SpecialTermsRequest>({
          method: 'post',
          url: `/deals/${dealID}/special-terms-requests/${requestID}/reject`,
          data: payload,
          headers: {
            'Content-Type': 'application/json',
          },
        })
        .then((res) => res.data),
    getPublicProposal: (token: string) =>
      axios
        .request<Deal>({
          method: 'get',
          url: `/public/proposals/${token}`,
        })
        .then((res) => res.data),
    leaveProposalContacts: (payload: Contacts, token: string) =>
      axios
        .request<Problem>({
          method: 'post',
          url: `/public/proposals/${token}/contacts`,
          data: payload,
          headers: {
            'Content-Type': 'application/json',
          },
        })
        .then((res) => res.data),
    acceptPublicProposal: (payload: AcceptProposal, token: string) =>
      axios
        .request<AcceptProposalResponse>({
          method: 'post',
          url: `/public/proposals/${token}/accept`,
          data: payload,
          headers: {
            'Content-Type': 'application/json',
          },
        })
        .then((res) => res.data),
    declinePublicProposal: (payload: ReasonRequest, token: string) =>
      axios
        .request<Problem>({
          method: 'post',
          url: `/public/proposals/${token}/decline`,
          data: payload,
          headers: {
            'Content-Type': 'application/json',
          },
        })
        .then((res) => res.data),
    getAnalyticsFunnel: (from?: string, to?: string) =>
      axios
        .request<Funnel>({
          method: 'get',
          url: `/analytics/funnel`,
          params: {
            ...(from !== undefined ? { from } : undefined),
            ...(to !== undefined ? { to } : undefined),
          },
          paramsSerializer: config?.paramsSerializer,
        })
        .then((res) => res.data),
    trackUIEvent: (payload: TrackingEvent) =>
      axios
        .request<Problem>({
          method: 'post',
          url: `/tracking/events`,
          data: payload,
          headers: {
            'Content-Type': 'application/json',
          },
        })
        .then((res) => res.data),
  } as const;
}
export type Requests = ReturnType<typeof makeRequests>;
export type Response<T extends keyof Requests> = Awaited<
  ReturnType<Requests[T]>
>;
function makeQueries(requests: Requests) {
  return {
    useGetMe: (
      options?: Omit<
        UseQueryOptions<
          Response<'getMe'>,
          unknown,
          Response<'getMe'>,
          ReturnType<QueryKeys['getMe']>
        >,
        'queryKey' | 'queryFn'
      >,
    ): UseQueryResult<Response<'getMe'>, unknown> =>
      useQuery({
        queryKey: queryKeys.getMe(),
        queryFn: () => requests.getMe(),
        ...options,
      }),
    useGetMyDashboard: (
      options?: Omit<
        UseQueryOptions<
          Response<'getMyDashboard'>,
          unknown,
          Response<'getMyDashboard'>,
          ReturnType<QueryKeys['getMyDashboard']>
        >,
        'queryKey' | 'queryFn'
      >,
    ): UseQueryResult<Response<'getMyDashboard'>, unknown> =>
      useQuery({
        queryKey: queryKeys.getMyDashboard(),
        queryFn: () => requests.getMyDashboard(),
        ...options,
      }),
    useListUsers: (
      page?: number,
      limit?: number,
      status?: UserStatus,
      role?: UserRole,
      search?: string,
      options?: Omit<
        UseQueryOptions<
          Response<'listUsers'>,
          unknown,
          Response<'listUsers'>,
          ReturnType<QueryKeys['listUsers']>
        >,
        'queryKey' | 'queryFn'
      >,
    ): UseQueryResult<Response<'listUsers'>, unknown> =>
      useQuery({
        queryKey: queryKeys.listUsers(page, limit, status, role, search),
        queryFn: () => requests.listUsers(page, limit, status, role, search),
        ...options,
      }),
    useListRegistrationInvites: (
      page?: number,
      limit?: number,
      options?: Omit<
        UseQueryOptions<
          Response<'listRegistrationInvites'>,
          unknown,
          Response<'listRegistrationInvites'>,
          ReturnType<QueryKeys['listRegistrationInvites']>
        >,
        'queryKey' | 'queryFn'
      >,
    ): UseQueryResult<Response<'listRegistrationInvites'>, unknown> =>
      useQuery({
        queryKey: queryKeys.listRegistrationInvites(page, limit),
        queryFn: () => requests.listRegistrationInvites(page, limit),
        ...options,
      }),
    useGetUser: (
      userID: string,
      options?: Omit<
        UseQueryOptions<
          Response<'getUser'>,
          unknown,
          Response<'getUser'>,
          ReturnType<QueryKeys['getUser']>
        >,
        'queryKey' | 'queryFn'
      >,
    ): UseQueryResult<Response<'getUser'>, unknown> =>
      useQuery({
        queryKey: queryKeys.getUser(userID),
        queryFn: () => requests.getUser(userID),
        ...options,
      }),
    useListAuditEvents: (
      page?: number,
      limit?: number,
      actor_user_id?: string,
      action?: string,
      entity_type?: string,
      entity_id?: string,
      from?: string,
      to?: string,
      options?: Omit<
        UseQueryOptions<
          Response<'listAuditEvents'>,
          unknown,
          Response<'listAuditEvents'>,
          ReturnType<QueryKeys['listAuditEvents']>
        >,
        'queryKey' | 'queryFn'
      >,
    ): UseQueryResult<Response<'listAuditEvents'>, unknown> =>
      useQuery({
        queryKey: queryKeys.listAuditEvents(
          page,
          limit,
          actor_user_id,
          action,
          entity_type,
          entity_id,
          from,
          to,
        ),
        queryFn: () =>
          requests.listAuditEvents(
            page,
            limit,
            actor_user_id,
            action,
            entity_type,
            entity_id,
            from,
            to,
          ),
        ...options,
      }),
    useGetAuditEvent: (
      eventID: string,
      options?: Omit<
        UseQueryOptions<
          Response<'getAuditEvent'>,
          unknown,
          Response<'getAuditEvent'>,
          ReturnType<QueryKeys['getAuditEvent']>
        >,
        'queryKey' | 'queryFn'
      >,
    ): UseQueryResult<Response<'getAuditEvent'>, unknown> =>
      useQuery({
        queryKey: queryKeys.getAuditEvent(eventID),
        queryFn: () => requests.getAuditEvent(eventID),
        ...options,
      }),
    useGetCatalogFacets: (
      statuses?: AvailabilityStatus[],
      provider_ids?: string[],
      countries?: string[],
      currencies?: string[],
      traffic_types?: TrafficType[],
      integration_methods?: string[],
      options?: Omit<
        UseQueryOptions<
          Response<'getCatalogFacets'>,
          unknown,
          Response<'getCatalogFacets'>,
          ReturnType<QueryKeys['getCatalogFacets']>
        >,
        'queryKey' | 'queryFn'
      >,
    ): UseQueryResult<Response<'getCatalogFacets'>, unknown> =>
      useQuery({
        queryKey: queryKeys.getCatalogFacets(
          statuses,
          provider_ids,
          countries,
          currencies,
          traffic_types,
          integration_methods,
        ),
        queryFn: () =>
          requests.getCatalogFacets(
            statuses,
            provider_ids,
            countries,
            currencies,
            traffic_types,
            integration_methods,
          ),
        ...options,
      }),
    useListCatalogOffers: (
      page?: number,
      limit?: number,
      search?: string,
      sort?: 'newest' | 'oldest',
      statuses?: AvailabilityStatus[],
      provider_ids?: string[],
      countries?: string[],
      currencies?: string[],
      traffic_types?: TrafficType[],
      integration_methods?: string[],
      options?: Omit<
        UseQueryOptions<
          Response<'listCatalogOffers'>,
          unknown,
          Response<'listCatalogOffers'>,
          ReturnType<QueryKeys['listCatalogOffers']>
        >,
        'queryKey' | 'queryFn'
      >,
    ): UseQueryResult<Response<'listCatalogOffers'>, unknown> =>
      useQuery({
        queryKey: queryKeys.listCatalogOffers(
          page,
          limit,
          search,
          sort,
          statuses,
          provider_ids,
          countries,
          currencies,
          traffic_types,
          integration_methods,
        ),
        queryFn: () =>
          requests.listCatalogOffers(
            page,
            limit,
            search,
            sort,
            statuses,
            provider_ids,
            countries,
            currencies,
            traffic_types,
            integration_methods,
          ),
        ...options,
      }),
    useGetCatalogOffer: (
      offerID: string,
      options?: Omit<
        UseQueryOptions<
          Response<'getCatalogOffer'>,
          unknown,
          Response<'getCatalogOffer'>,
          ReturnType<QueryKeys['getCatalogOffer']>
        >,
        'queryKey' | 'queryFn'
      >,
    ): UseQueryResult<Response<'getCatalogOffer'>, unknown> =>
      useQuery({
        queryKey: queryKeys.getCatalogOffer(offerID),
        queryFn: () => requests.getCatalogOffer(offerID),
        ...options,
      }),
    useListProviders: (
      page?: number,
      limit?: number,
      status?: AvailabilityStatus,
      search?: string,
      country?: string[],
      currency?: string[],
      integration_method?: string[],
      payment_method?: string[],
      industry?: string[],
      is_deleted?: boolean,
      options?: Omit<
        UseQueryOptions<
          Response<'listProviders'>,
          unknown,
          Response<'listProviders'>,
          ReturnType<QueryKeys['listProviders']>
        >,
        'queryKey' | 'queryFn'
      >,
    ): UseQueryResult<Response<'listProviders'>, unknown> =>
      useQuery({
        queryKey: queryKeys.listProviders(
          page,
          limit,
          status,
          search,
          country,
          currency,
          integration_method,
          payment_method,
          industry,
          is_deleted,
        ),
        queryFn: () =>
          requests.listProviders(
            page,
            limit,
            status,
            search,
            country,
            currency,
            integration_method,
            payment_method,
            industry,
            is_deleted,
          ),
        ...options,
      }),
    useGetProviderOptions: (
      options?: Omit<
        UseQueryOptions<
          Response<'getProviderOptions'>,
          unknown,
          Response<'getProviderOptions'>,
          ReturnType<QueryKeys['getProviderOptions']>
        >,
        'queryKey' | 'queryFn'
      >,
    ): UseQueryResult<Response<'getProviderOptions'>, unknown> =>
      useQuery({
        queryKey: queryKeys.getProviderOptions(),
        queryFn: () => requests.getProviderOptions(),
        ...options,
      }),
    useGetProvider: (
      providerID: string,
      options?: Omit<
        UseQueryOptions<
          Response<'getProvider'>,
          unknown,
          Response<'getProvider'>,
          ReturnType<QueryKeys['getProvider']>
        >,
        'queryKey' | 'queryFn'
      >,
    ): UseQueryResult<Response<'getProvider'>, unknown> =>
      useQuery({
        queryKey: queryKeys.getProvider(providerID),
        queryFn: () => requests.getProvider(providerID),
        ...options,
      }),
    useGetProviderOfferCapabilities: (
      providerID: string,
      options?: Omit<
        UseQueryOptions<
          Response<'getProviderOfferCapabilities'>,
          unknown,
          Response<'getProviderOfferCapabilities'>,
          ReturnType<QueryKeys['getProviderOfferCapabilities']>
        >,
        'queryKey' | 'queryFn'
      >,
    ): UseQueryResult<Response<'getProviderOfferCapabilities'>, unknown> =>
      useQuery({
        queryKey: queryKeys.getProviderOfferCapabilities(providerID),
        queryFn: () => requests.getProviderOfferCapabilities(providerID),
        ...options,
      }),
    useGetProviderAvailabilityImpact: (
      providerID: string,
      options?: Omit<
        UseQueryOptions<
          Response<'getProviderAvailabilityImpact'>,
          unknown,
          Response<'getProviderAvailabilityImpact'>,
          ReturnType<QueryKeys['getProviderAvailabilityImpact']>
        >,
        'queryKey' | 'queryFn'
      >,
    ): UseQueryResult<Response<'getProviderAvailabilityImpact'>, unknown> =>
      useQuery({
        queryKey: queryKeys.getProviderAvailabilityImpact(providerID),
        queryFn: () => requests.getProviderAvailabilityImpact(providerID),
        ...options,
      }),
    useListAdminOffers: (
      page?: number,
      limit?: number,
      search?: string,
      sort?: 'newest' | 'oldest',
      statuses?: AvailabilityStatus[],
      provider_ids?: string[],
      countries?: string[],
      currencies?: string[],
      traffic_types?: TrafficType[],
      integration_methods?: string[],
      is_deleted?: boolean,
      options?: Omit<
        UseQueryOptions<
          Response<'listAdminOffers'>,
          unknown,
          Response<'listAdminOffers'>,
          ReturnType<QueryKeys['listAdminOffers']>
        >,
        'queryKey' | 'queryFn'
      >,
    ): UseQueryResult<Response<'listAdminOffers'>, unknown> =>
      useQuery({
        queryKey: queryKeys.listAdminOffers(
          page,
          limit,
          search,
          sort,
          statuses,
          provider_ids,
          countries,
          currencies,
          traffic_types,
          integration_methods,
          is_deleted,
        ),
        queryFn: () =>
          requests.listAdminOffers(
            page,
            limit,
            search,
            sort,
            statuses,
            provider_ids,
            countries,
            currencies,
            traffic_types,
            integration_methods,
            is_deleted,
          ),
        ...options,
      }),
    useGetAdminOffer: (
      offerID: string,
      options?: Omit<
        UseQueryOptions<
          Response<'getAdminOffer'>,
          unknown,
          Response<'getAdminOffer'>,
          ReturnType<QueryKeys['getAdminOffer']>
        >,
        'queryKey' | 'queryFn'
      >,
    ): UseQueryResult<Response<'getAdminOffer'>, unknown> =>
      useQuery({
        queryKey: queryKeys.getAdminOffer(offerID),
        queryFn: () => requests.getAdminOffer(offerID),
        ...options,
      }),
    useGetOfferDuplicateDraft: (
      offerID: string,
      options?: Omit<
        UseQueryOptions<
          Response<'getOfferDuplicateDraft'>,
          unknown,
          Response<'getOfferDuplicateDraft'>,
          ReturnType<QueryKeys['getOfferDuplicateDraft']>
        >,
        'queryKey' | 'queryFn'
      >,
    ): UseQueryResult<Response<'getOfferDuplicateDraft'>, unknown> =>
      useQuery({
        queryKey: queryKeys.getOfferDuplicateDraft(offerID),
        queryFn: () => requests.getOfferDuplicateDraft(offerID),
        ...options,
      }),
    useListDeals: (
      page?: number,
      limit?: number,
      status?: DealStatus,
      source?: DealSource,
      options?: Omit<
        UseQueryOptions<
          Response<'listDeals'>,
          unknown,
          Response<'listDeals'>,
          ReturnType<QueryKeys['listDeals']>
        >,
        'queryKey' | 'queryFn'
      >,
    ): UseQueryResult<Response<'listDeals'>, unknown> =>
      useQuery({
        queryKey: queryKeys.listDeals(page, limit, status, source),
        queryFn: () => requests.listDeals(page, limit, status, source),
        ...options,
      }),
    useGetDeal: (
      dealID: string,
      options?: Omit<
        UseQueryOptions<
          Response<'getDeal'>,
          unknown,
          Response<'getDeal'>,
          ReturnType<QueryKeys['getDeal']>
        >,
        'queryKey' | 'queryFn'
      >,
    ): UseQueryResult<Response<'getDeal'>, unknown> =>
      useQuery({
        queryKey: queryKeys.getDeal(dealID),
        queryFn: () => requests.getDeal(dealID),
        ...options,
      }),
    useGetDealEvents: (
      dealID: string,
      page?: number,
      limit?: number,
      options?: Omit<
        UseQueryOptions<
          Response<'getDealEvents'>,
          unknown,
          Response<'getDealEvents'>,
          ReturnType<QueryKeys['getDealEvents']>
        >,
        'queryKey' | 'queryFn'
      >,
    ): UseQueryResult<Response<'getDealEvents'>, unknown> =>
      useQuery({
        queryKey: queryKeys.getDealEvents(dealID, page, limit),
        queryFn: () => requests.getDealEvents(dealID, page, limit),
        ...options,
      }),
    useGetPublicProposal: (
      token: string,
      options?: Omit<
        UseQueryOptions<
          Response<'getPublicProposal'>,
          unknown,
          Response<'getPublicProposal'>,
          ReturnType<QueryKeys['getPublicProposal']>
        >,
        'queryKey' | 'queryFn'
      >,
    ): UseQueryResult<Response<'getPublicProposal'>, unknown> =>
      useQuery({
        queryKey: queryKeys.getPublicProposal(token),
        queryFn: () => requests.getPublicProposal(token),
        ...options,
      }),
    useGetAnalyticsFunnel: (
      from?: string,
      to?: string,
      options?: Omit<
        UseQueryOptions<
          Response<'getAnalyticsFunnel'>,
          unknown,
          Response<'getAnalyticsFunnel'>,
          ReturnType<QueryKeys['getAnalyticsFunnel']>
        >,
        'queryKey' | 'queryFn'
      >,
    ): UseQueryResult<Response<'getAnalyticsFunnel'>, unknown> =>
      useQuery({
        queryKey: queryKeys.getAnalyticsFunnel(from, to),
        queryFn: () => requests.getAnalyticsFunnel(from, to),
        ...options,
      }),
  } as const;
}
type MutationConfigs = {
  useRegister?: (
    queryClient: QueryClient,
  ) => Pick<
    UseMutationOptions<
      Response<'register'>,
      unknown,
      Parameters<Requests['register']>[0],
      unknown
    >,
    'onSuccess' | 'onSettled' | 'onError'
  >;
  useLogin?: (
    queryClient: QueryClient,
  ) => Pick<
    UseMutationOptions<
      Response<'login'>,
      unknown,
      Parameters<Requests['login']>[0],
      unknown
    >,
    'onSuccess' | 'onSettled' | 'onError'
  >;
  useRefreshTokens?: (
    queryClient: QueryClient,
  ) => Pick<
    UseMutationOptions<
      Response<'refreshTokens'>,
      unknown,
      Parameters<Requests['refreshTokens']>[0],
      unknown
    >,
    'onSuccess' | 'onSettled' | 'onError'
  >;
  useLogout?: (
    queryClient: QueryClient,
  ) => Pick<
    UseMutationOptions<
      Response<'logout'>,
      unknown,
      Parameters<Requests['logout']>[0],
      unknown
    >,
    'onSuccess' | 'onSettled' | 'onError'
  >;
  useChangePassword?: (
    queryClient: QueryClient,
  ) => Pick<
    UseMutationOptions<
      Response<'changePassword'>,
      unknown,
      Parameters<Requests['changePassword']>[0],
      unknown
    >,
    'onSuccess' | 'onSettled' | 'onError'
  >;
  useUpdateMe?: (
    queryClient: QueryClient,
  ) => Pick<
    UseMutationOptions<
      Response<'updateMe'>,
      unknown,
      Parameters<Requests['updateMe']>[0],
      unknown
    >,
    'onSuccess' | 'onSettled' | 'onError'
  >;
  useCreateRegistrationInvite?: (
    queryClient: QueryClient,
  ) => Pick<
    UseMutationOptions<
      Response<'createRegistrationInvite'>,
      unknown,
      unknown,
      unknown
    >,
    'onSuccess' | 'onSettled' | 'onError'
  >;
  useSetUserRole?: (
    queryClient: QueryClient,
  ) => Pick<
    UseMutationOptions<
      Response<'setUserRole'>,
      unknown,
      Parameters<Requests['setUserRole']>[0],
      unknown
    >,
    'onSuccess' | 'onSettled' | 'onError'
  >;
  useSetUserStatus?: (
    queryClient: QueryClient,
  ) => Pick<
    UseMutationOptions<
      Response<'setUserStatus'>,
      unknown,
      Parameters<Requests['setUserStatus']>[0],
      unknown
    >,
    'onSuccess' | 'onSettled' | 'onError'
  >;
  useUploadProviderFiles?: (
    queryClient: QueryClient,
  ) => Pick<
    UseMutationOptions<
      Response<'uploadProviderFiles'>,
      unknown,
      Parameters<Requests['uploadProviderFiles']>[0],
      unknown
    >,
    'onSuccess' | 'onSettled' | 'onError'
  >;
  useCreateProvider?: (
    queryClient: QueryClient,
  ) => Pick<
    UseMutationOptions<
      Response<'createProvider'>,
      unknown,
      Parameters<Requests['createProvider']>[0],
      unknown
    >,
    'onSuccess' | 'onSettled' | 'onError'
  >;
  useUpdateProvider?: (
    queryClient: QueryClient,
  ) => Pick<
    UseMutationOptions<
      Response<'updateProvider'>,
      unknown,
      Parameters<Requests['updateProvider']>[0],
      unknown
    >,
    'onSuccess' | 'onSettled' | 'onError'
  >;
  useDeleteProvider?: (
    queryClient: QueryClient,
  ) => Pick<
    UseMutationOptions<Response<'deleteProvider'>, unknown, unknown, unknown>,
    'onSuccess' | 'onSettled' | 'onError'
  >;
  useRestoreProvider?: (
    queryClient: QueryClient,
  ) => Pick<
    UseMutationOptions<Response<'restoreProvider'>, unknown, unknown, unknown>,
    'onSuccess' | 'onSettled' | 'onError'
  >;
  useSetProviderStatus?: (
    queryClient: QueryClient,
  ) => Pick<
    UseMutationOptions<
      Response<'setProviderStatus'>,
      unknown,
      Parameters<Requests['setProviderStatus']>[0],
      unknown
    >,
    'onSuccess' | 'onSettled' | 'onError'
  >;
  useCreateOffer?: (
    queryClient: QueryClient,
  ) => Pick<
    UseMutationOptions<
      Response<'createOffer'>,
      unknown,
      Parameters<Requests['createOffer']>[0],
      unknown
    >,
    'onSuccess' | 'onSettled' | 'onError'
  >;
  useUpdateOffer?: (
    queryClient: QueryClient,
  ) => Pick<
    UseMutationOptions<
      Response<'updateOffer'>,
      unknown,
      Parameters<Requests['updateOffer']>[0],
      unknown
    >,
    'onSuccess' | 'onSettled' | 'onError'
  >;
  useDeleteOffer?: (
    queryClient: QueryClient,
  ) => Pick<
    UseMutationOptions<Response<'deleteOffer'>, unknown, unknown, unknown>,
    'onSuccess' | 'onSettled' | 'onError'
  >;
  useRestoreOffer?: (
    queryClient: QueryClient,
  ) => Pick<
    UseMutationOptions<Response<'restoreOffer'>, unknown, unknown, unknown>,
    'onSuccess' | 'onSettled' | 'onError'
  >;
  useSetOfferStatus?: (
    queryClient: QueryClient,
  ) => Pick<
    UseMutationOptions<
      Response<'setOfferStatus'>,
      unknown,
      Parameters<Requests['setOfferStatus']>[0],
      unknown
    >,
    'onSuccess' | 'onSettled' | 'onError'
  >;
  useBulkSetOfferStatus?: (
    queryClient: QueryClient,
  ) => Pick<
    UseMutationOptions<
      Response<'bulkSetOfferStatus'>,
      unknown,
      Parameters<Requests['bulkSetOfferStatus']>[0],
      unknown
    >,
    'onSuccess' | 'onSettled' | 'onError'
  >;
  useCreateDeal?: (
    queryClient: QueryClient,
  ) => Pick<
    UseMutationOptions<
      Response<'createDeal'>,
      unknown,
      Parameters<Requests['createDeal']>[0],
      unknown
    >,
    'onSuccess' | 'onSettled' | 'onError'
  >;
  useUpdateDraftDeal?: (
    queryClient: QueryClient,
  ) => Pick<
    UseMutationOptions<
      Response<'updateDraftDeal'>,
      unknown,
      Parameters<Requests['updateDraftDeal']>[0],
      unknown
    >,
    'onSuccess' | 'onSettled' | 'onError'
  >;
  useAddDealItem?: (
    queryClient: QueryClient,
  ) => Pick<
    UseMutationOptions<
      Response<'addDealItem'>,
      unknown,
      Parameters<Requests['addDealItem']>[0],
      unknown
    >,
    'onSuccess' | 'onSettled' | 'onError'
  >;
  useUpdateDealItemMargin?: (
    queryClient: QueryClient,
  ) => Pick<
    UseMutationOptions<
      Response<'updateDealItemMargin'>,
      unknown,
      Parameters<Requests['updateDealItemMargin']>[0],
      unknown
    >,
    'onSuccess' | 'onSettled' | 'onError'
  >;
  useDeleteDealItem?: (
    queryClient: QueryClient,
  ) => Pick<
    UseMutationOptions<Response<'deleteDealItem'>, unknown, unknown, unknown>,
    'onSuccess' | 'onSettled' | 'onError'
  >;
  useGenerateDealLink?: (
    queryClient: QueryClient,
  ) => Pick<
    UseMutationOptions<Response<'generateDealLink'>, unknown, unknown, unknown>,
    'onSuccess' | 'onSettled' | 'onError'
  >;
  useRevokeDeal?: (
    queryClient: QueryClient,
  ) => Pick<
    UseMutationOptions<
      Response<'revokeDeal'>,
      unknown,
      Parameters<Requests['revokeDeal']>[0],
      unknown
    >,
    'onSuccess' | 'onSettled' | 'onError'
  >;
  useTerminateDeal?: (
    queryClient: QueryClient,
  ) => Pick<
    UseMutationOptions<
      Response<'terminateDeal'>,
      unknown,
      Parameters<Requests['terminateDeal']>[0],
      unknown
    >,
    'onSuccess' | 'onSettled' | 'onError'
  >;
  useArchiveDeal?: (
    queryClient: QueryClient,
  ) => Pick<
    UseMutationOptions<
      Response<'archiveDeal'>,
      unknown,
      Parameters<Requests['archiveDeal']>[0],
      unknown
    >,
    'onSuccess' | 'onSettled' | 'onError'
  >;
  useRequestSpecialTerms?: (
    queryClient: QueryClient,
  ) => Pick<
    UseMutationOptions<
      Response<'requestSpecialTerms'>,
      unknown,
      Parameters<Requests['requestSpecialTerms']>[0],
      unknown
    >,
    'onSuccess' | 'onSettled' | 'onError'
  >;
  useResolveSpecialTerms?: (
    queryClient: QueryClient,
  ) => Pick<
    UseMutationOptions<
      Response<'resolveSpecialTerms'>,
      unknown,
      Parameters<Requests['resolveSpecialTerms']>[0],
      unknown
    >,
    'onSuccess' | 'onSettled' | 'onError'
  >;
  useRejectSpecialTerms?: (
    queryClient: QueryClient,
  ) => Pick<
    UseMutationOptions<
      Response<'rejectSpecialTerms'>,
      unknown,
      Parameters<Requests['rejectSpecialTerms']>[0],
      unknown
    >,
    'onSuccess' | 'onSettled' | 'onError'
  >;
  useLeaveProposalContacts?: (
    queryClient: QueryClient,
  ) => Pick<
    UseMutationOptions<
      Response<'leaveProposalContacts'>,
      unknown,
      Parameters<Requests['leaveProposalContacts']>[0],
      unknown
    >,
    'onSuccess' | 'onSettled' | 'onError'
  >;
  useAcceptPublicProposal?: (
    queryClient: QueryClient,
  ) => Pick<
    UseMutationOptions<
      Response<'acceptPublicProposal'>,
      unknown,
      Parameters<Requests['acceptPublicProposal']>[0],
      unknown
    >,
    'onSuccess' | 'onSettled' | 'onError'
  >;
  useDeclinePublicProposal?: (
    queryClient: QueryClient,
  ) => Pick<
    UseMutationOptions<
      Response<'declinePublicProposal'>,
      unknown,
      Parameters<Requests['declinePublicProposal']>[0],
      unknown
    >,
    'onSuccess' | 'onSettled' | 'onError'
  >;
  useTrackUIEvent?: (
    queryClient: QueryClient,
  ) => Pick<
    UseMutationOptions<
      Response<'trackUIEvent'>,
      unknown,
      Parameters<Requests['trackUIEvent']>[0],
      unknown
    >,
    'onSuccess' | 'onSettled' | 'onError'
  >;
};
function makeMutations(requests: Requests, config?: Config['mutations']) {
  return {
    useRegister: (
      options?: Omit<
        UseMutationOptions<
          Response<'register'>,
          unknown,
          Parameters<Requests['register']>[0],
          unknown
        >,
        'mutationFn'
      >,
    ) =>
      useRapiniMutation<
        Response<'register'>,
        unknown,
        Parameters<Requests['register']>[0]
      >((payload) => requests.register(payload), config?.useRegister, options),
    useLogin: (
      options?: Omit<
        UseMutationOptions<
          Response<'login'>,
          unknown,
          Parameters<Requests['login']>[0],
          unknown
        >,
        'mutationFn'
      >,
    ) =>
      useRapiniMutation<
        Response<'login'>,
        unknown,
        Parameters<Requests['login']>[0]
      >((payload) => requests.login(payload), config?.useLogin, options),
    useRefreshTokens: (
      options?: Omit<
        UseMutationOptions<
          Response<'refreshTokens'>,
          unknown,
          Parameters<Requests['refreshTokens']>[0],
          unknown
        >,
        'mutationFn'
      >,
    ) =>
      useRapiniMutation<
        Response<'refreshTokens'>,
        unknown,
        Parameters<Requests['refreshTokens']>[0]
      >(
        (payload) => requests.refreshTokens(payload),
        config?.useRefreshTokens,
        options,
      ),
    useLogout: (
      options?: Omit<
        UseMutationOptions<
          Response<'logout'>,
          unknown,
          Parameters<Requests['logout']>[0],
          unknown
        >,
        'mutationFn'
      >,
    ) =>
      useRapiniMutation<
        Response<'logout'>,
        unknown,
        Parameters<Requests['logout']>[0]
      >((payload) => requests.logout(payload), config?.useLogout, options),
    useChangePassword: (
      options?: Omit<
        UseMutationOptions<
          Response<'changePassword'>,
          unknown,
          Parameters<Requests['changePassword']>[0],
          unknown
        >,
        'mutationFn'
      >,
    ) =>
      useRapiniMutation<
        Response<'changePassword'>,
        unknown,
        Parameters<Requests['changePassword']>[0]
      >(
        (payload) => requests.changePassword(payload),
        config?.useChangePassword,
        options,
      ),
    useUpdateMe: (
      options?: Omit<
        UseMutationOptions<
          Response<'updateMe'>,
          unknown,
          Parameters<Requests['updateMe']>[0],
          unknown
        >,
        'mutationFn'
      >,
    ) =>
      useRapiniMutation<
        Response<'updateMe'>,
        unknown,
        Parameters<Requests['updateMe']>[0]
      >((payload) => requests.updateMe(payload), config?.useUpdateMe, options),
    useCreateRegistrationInvite: (
      options?: Omit<
        UseMutationOptions<
          Response<'createRegistrationInvite'>,
          unknown,
          unknown,
          unknown
        >,
        'mutationFn'
      >,
    ) =>
      useRapiniMutation<Response<'createRegistrationInvite'>, unknown, unknown>(
        () => requests.createRegistrationInvite(),
        config?.useCreateRegistrationInvite,
        options,
      ),
    useSetUserRole: (
      userID: string,
      options?: Omit<
        UseMutationOptions<
          Response<'setUserRole'>,
          unknown,
          Parameters<Requests['setUserRole']>[0],
          unknown
        >,
        'mutationFn'
      >,
    ) =>
      useRapiniMutation<
        Response<'setUserRole'>,
        unknown,
        Parameters<Requests['setUserRole']>[0]
      >(
        (payload) => requests.setUserRole(payload, userID),
        config?.useSetUserRole,
        options,
      ),
    useSetUserStatus: (
      userID: string,
      options?: Omit<
        UseMutationOptions<
          Response<'setUserStatus'>,
          unknown,
          Parameters<Requests['setUserStatus']>[0],
          unknown
        >,
        'mutationFn'
      >,
    ) =>
      useRapiniMutation<
        Response<'setUserStatus'>,
        unknown,
        Parameters<Requests['setUserStatus']>[0]
      >(
        (payload) => requests.setUserStatus(payload, userID),
        config?.useSetUserStatus,
        options,
      ),
    useUploadProviderFiles: (
      options?: Omit<
        UseMutationOptions<
          Response<'uploadProviderFiles'>,
          unknown,
          Parameters<Requests['uploadProviderFiles']>[0],
          unknown
        >,
        'mutationFn'
      >,
    ) =>
      useRapiniMutation<
        Response<'uploadProviderFiles'>,
        unknown,
        Parameters<Requests['uploadProviderFiles']>[0]
      >(
        (payload) => requests.uploadProviderFiles(payload),
        config?.useUploadProviderFiles,
        options,
      ),
    useCreateProvider: (
      options?: Omit<
        UseMutationOptions<
          Response<'createProvider'>,
          unknown,
          Parameters<Requests['createProvider']>[0],
          unknown
        >,
        'mutationFn'
      >,
    ) =>
      useRapiniMutation<
        Response<'createProvider'>,
        unknown,
        Parameters<Requests['createProvider']>[0]
      >(
        (payload) => requests.createProvider(payload),
        config?.useCreateProvider,
        options,
      ),
    useUpdateProvider: (
      providerID: string,
      options?: Omit<
        UseMutationOptions<
          Response<'updateProvider'>,
          unknown,
          Parameters<Requests['updateProvider']>[0],
          unknown
        >,
        'mutationFn'
      >,
    ) =>
      useRapiniMutation<
        Response<'updateProvider'>,
        unknown,
        Parameters<Requests['updateProvider']>[0]
      >(
        (payload) => requests.updateProvider(payload, providerID),
        config?.useUpdateProvider,
        options,
      ),
    useDeleteProvider: (
      providerID: string,
      options?: Omit<
        UseMutationOptions<
          Response<'deleteProvider'>,
          unknown,
          unknown,
          unknown
        >,
        'mutationFn'
      >,
    ) =>
      useRapiniMutation<Response<'deleteProvider'>, unknown, unknown>(
        () => requests.deleteProvider(providerID),
        config?.useDeleteProvider,
        options,
      ),
    useRestoreProvider: (
      providerID: string,
      options?: Omit<
        UseMutationOptions<
          Response<'restoreProvider'>,
          unknown,
          unknown,
          unknown
        >,
        'mutationFn'
      >,
    ) =>
      useRapiniMutation<Response<'restoreProvider'>, unknown, unknown>(
        () => requests.restoreProvider(providerID),
        config?.useRestoreProvider,
        options,
      ),
    useSetProviderStatus: (
      providerID: string,
      options?: Omit<
        UseMutationOptions<
          Response<'setProviderStatus'>,
          unknown,
          Parameters<Requests['setProviderStatus']>[0],
          unknown
        >,
        'mutationFn'
      >,
    ) =>
      useRapiniMutation<
        Response<'setProviderStatus'>,
        unknown,
        Parameters<Requests['setProviderStatus']>[0]
      >(
        (payload) => requests.setProviderStatus(payload, providerID),
        config?.useSetProviderStatus,
        options,
      ),
    useCreateOffer: (
      options?: Omit<
        UseMutationOptions<
          Response<'createOffer'>,
          unknown,
          Parameters<Requests['createOffer']>[0],
          unknown
        >,
        'mutationFn'
      >,
    ) =>
      useRapiniMutation<
        Response<'createOffer'>,
        unknown,
        Parameters<Requests['createOffer']>[0]
      >(
        (payload) => requests.createOffer(payload),
        config?.useCreateOffer,
        options,
      ),
    useUpdateOffer: (
      offerID: string,
      options?: Omit<
        UseMutationOptions<
          Response<'updateOffer'>,
          unknown,
          Parameters<Requests['updateOffer']>[0],
          unknown
        >,
        'mutationFn'
      >,
    ) =>
      useRapiniMutation<
        Response<'updateOffer'>,
        unknown,
        Parameters<Requests['updateOffer']>[0]
      >(
        (payload) => requests.updateOffer(payload, offerID),
        config?.useUpdateOffer,
        options,
      ),
    useDeleteOffer: (
      offerID: string,
      options?: Omit<
        UseMutationOptions<Response<'deleteOffer'>, unknown, unknown, unknown>,
        'mutationFn'
      >,
    ) =>
      useRapiniMutation<Response<'deleteOffer'>, unknown, unknown>(
        () => requests.deleteOffer(offerID),
        config?.useDeleteOffer,
        options,
      ),
    useRestoreOffer: (
      offerID: string,
      options?: Omit<
        UseMutationOptions<Response<'restoreOffer'>, unknown, unknown, unknown>,
        'mutationFn'
      >,
    ) =>
      useRapiniMutation<Response<'restoreOffer'>, unknown, unknown>(
        () => requests.restoreOffer(offerID),
        config?.useRestoreOffer,
        options,
      ),
    useSetOfferStatus: (
      offerID: string,
      options?: Omit<
        UseMutationOptions<
          Response<'setOfferStatus'>,
          unknown,
          Parameters<Requests['setOfferStatus']>[0],
          unknown
        >,
        'mutationFn'
      >,
    ) =>
      useRapiniMutation<
        Response<'setOfferStatus'>,
        unknown,
        Parameters<Requests['setOfferStatus']>[0]
      >(
        (payload) => requests.setOfferStatus(payload, offerID),
        config?.useSetOfferStatus,
        options,
      ),
    useBulkSetOfferStatus: (
      options?: Omit<
        UseMutationOptions<
          Response<'bulkSetOfferStatus'>,
          unknown,
          Parameters<Requests['bulkSetOfferStatus']>[0],
          unknown
        >,
        'mutationFn'
      >,
    ) =>
      useRapiniMutation<
        Response<'bulkSetOfferStatus'>,
        unknown,
        Parameters<Requests['bulkSetOfferStatus']>[0]
      >(
        (payload) => requests.bulkSetOfferStatus(payload),
        config?.useBulkSetOfferStatus,
        options,
      ),
    useCreateDeal: (
      options?: Omit<
        UseMutationOptions<
          Response<'createDeal'>,
          unknown,
          Parameters<Requests['createDeal']>[0],
          unknown
        >,
        'mutationFn'
      >,
    ) =>
      useRapiniMutation<
        Response<'createDeal'>,
        unknown,
        Parameters<Requests['createDeal']>[0]
      >(
        (payload) => requests.createDeal(payload),
        config?.useCreateDeal,
        options,
      ),
    useUpdateDraftDeal: (
      dealID: string,
      options?: Omit<
        UseMutationOptions<
          Response<'updateDraftDeal'>,
          unknown,
          Parameters<Requests['updateDraftDeal']>[0],
          unknown
        >,
        'mutationFn'
      >,
    ) =>
      useRapiniMutation<
        Response<'updateDraftDeal'>,
        unknown,
        Parameters<Requests['updateDraftDeal']>[0]
      >(
        (payload) => requests.updateDraftDeal(payload, dealID),
        config?.useUpdateDraftDeal,
        options,
      ),
    useAddDealItem: (
      dealID: string,
      options?: Omit<
        UseMutationOptions<
          Response<'addDealItem'>,
          unknown,
          Parameters<Requests['addDealItem']>[0],
          unknown
        >,
        'mutationFn'
      >,
    ) =>
      useRapiniMutation<
        Response<'addDealItem'>,
        unknown,
        Parameters<Requests['addDealItem']>[0]
      >(
        (payload) => requests.addDealItem(payload, dealID),
        config?.useAddDealItem,
        options,
      ),
    useUpdateDealItemMargin: (
      dealID: string,
      itemID: string,
      options?: Omit<
        UseMutationOptions<
          Response<'updateDealItemMargin'>,
          unknown,
          Parameters<Requests['updateDealItemMargin']>[0],
          unknown
        >,
        'mutationFn'
      >,
    ) =>
      useRapiniMutation<
        Response<'updateDealItemMargin'>,
        unknown,
        Parameters<Requests['updateDealItemMargin']>[0]
      >(
        (payload) => requests.updateDealItemMargin(payload, dealID, itemID),
        config?.useUpdateDealItemMargin,
        options,
      ),
    useDeleteDealItem: (
      dealID: string,
      itemID: string,
      options?: Omit<
        UseMutationOptions<
          Response<'deleteDealItem'>,
          unknown,
          unknown,
          unknown
        >,
        'mutationFn'
      >,
    ) =>
      useRapiniMutation<Response<'deleteDealItem'>, unknown, unknown>(
        () => requests.deleteDealItem(dealID, itemID),
        config?.useDeleteDealItem,
        options,
      ),
    useGenerateDealLink: (
      dealID: string,
      options?: Omit<
        UseMutationOptions<
          Response<'generateDealLink'>,
          unknown,
          unknown,
          unknown
        >,
        'mutationFn'
      >,
    ) =>
      useRapiniMutation<Response<'generateDealLink'>, unknown, unknown>(
        () => requests.generateDealLink(dealID),
        config?.useGenerateDealLink,
        options,
      ),
    useRevokeDeal: (
      dealID: string,
      options?: Omit<
        UseMutationOptions<
          Response<'revokeDeal'>,
          unknown,
          Parameters<Requests['revokeDeal']>[0],
          unknown
        >,
        'mutationFn'
      >,
    ) =>
      useRapiniMutation<
        Response<'revokeDeal'>,
        unknown,
        Parameters<Requests['revokeDeal']>[0]
      >(
        (payload) => requests.revokeDeal(payload, dealID),
        config?.useRevokeDeal,
        options,
      ),
    useTerminateDeal: (
      dealID: string,
      options?: Omit<
        UseMutationOptions<
          Response<'terminateDeal'>,
          unknown,
          Parameters<Requests['terminateDeal']>[0],
          unknown
        >,
        'mutationFn'
      >,
    ) =>
      useRapiniMutation<
        Response<'terminateDeal'>,
        unknown,
        Parameters<Requests['terminateDeal']>[0]
      >(
        (payload) => requests.terminateDeal(payload, dealID),
        config?.useTerminateDeal,
        options,
      ),
    useArchiveDeal: (
      dealID: string,
      options?: Omit<
        UseMutationOptions<
          Response<'archiveDeal'>,
          unknown,
          Parameters<Requests['archiveDeal']>[0],
          unknown
        >,
        'mutationFn'
      >,
    ) =>
      useRapiniMutation<
        Response<'archiveDeal'>,
        unknown,
        Parameters<Requests['archiveDeal']>[0]
      >(
        (payload) => requests.archiveDeal(payload, dealID),
        config?.useArchiveDeal,
        options,
      ),
    useRequestSpecialTerms: (
      dealID: string,
      options?: Omit<
        UseMutationOptions<
          Response<'requestSpecialTerms'>,
          unknown,
          Parameters<Requests['requestSpecialTerms']>[0],
          unknown
        >,
        'mutationFn'
      >,
    ) =>
      useRapiniMutation<
        Response<'requestSpecialTerms'>,
        unknown,
        Parameters<Requests['requestSpecialTerms']>[0]
      >(
        (payload) => requests.requestSpecialTerms(payload, dealID),
        config?.useRequestSpecialTerms,
        options,
      ),
    useResolveSpecialTerms: (
      dealID: string,
      requestID: string,
      options?: Omit<
        UseMutationOptions<
          Response<'resolveSpecialTerms'>,
          unknown,
          Parameters<Requests['resolveSpecialTerms']>[0],
          unknown
        >,
        'mutationFn'
      >,
    ) =>
      useRapiniMutation<
        Response<'resolveSpecialTerms'>,
        unknown,
        Parameters<Requests['resolveSpecialTerms']>[0]
      >(
        (payload) => requests.resolveSpecialTerms(payload, dealID, requestID),
        config?.useResolveSpecialTerms,
        options,
      ),
    useRejectSpecialTerms: (
      dealID: string,
      requestID: string,
      options?: Omit<
        UseMutationOptions<
          Response<'rejectSpecialTerms'>,
          unknown,
          Parameters<Requests['rejectSpecialTerms']>[0],
          unknown
        >,
        'mutationFn'
      >,
    ) =>
      useRapiniMutation<
        Response<'rejectSpecialTerms'>,
        unknown,
        Parameters<Requests['rejectSpecialTerms']>[0]
      >(
        (payload) => requests.rejectSpecialTerms(payload, dealID, requestID),
        config?.useRejectSpecialTerms,
        options,
      ),
    useLeaveProposalContacts: (
      token: string,
      options?: Omit<
        UseMutationOptions<
          Response<'leaveProposalContacts'>,
          unknown,
          Parameters<Requests['leaveProposalContacts']>[0],
          unknown
        >,
        'mutationFn'
      >,
    ) =>
      useRapiniMutation<
        Response<'leaveProposalContacts'>,
        unknown,
        Parameters<Requests['leaveProposalContacts']>[0]
      >(
        (payload) => requests.leaveProposalContacts(payload, token),
        config?.useLeaveProposalContacts,
        options,
      ),
    useAcceptPublicProposal: (
      token: string,
      options?: Omit<
        UseMutationOptions<
          Response<'acceptPublicProposal'>,
          unknown,
          Parameters<Requests['acceptPublicProposal']>[0],
          unknown
        >,
        'mutationFn'
      >,
    ) =>
      useRapiniMutation<
        Response<'acceptPublicProposal'>,
        unknown,
        Parameters<Requests['acceptPublicProposal']>[0]
      >(
        (payload) => requests.acceptPublicProposal(payload, token),
        config?.useAcceptPublicProposal,
        options,
      ),
    useDeclinePublicProposal: (
      token: string,
      options?: Omit<
        UseMutationOptions<
          Response<'declinePublicProposal'>,
          unknown,
          Parameters<Requests['declinePublicProposal']>[0],
          unknown
        >,
        'mutationFn'
      >,
    ) =>
      useRapiniMutation<
        Response<'declinePublicProposal'>,
        unknown,
        Parameters<Requests['declinePublicProposal']>[0]
      >(
        (payload) => requests.declinePublicProposal(payload, token),
        config?.useDeclinePublicProposal,
        options,
      ),
    useTrackUIEvent: (
      options?: Omit<
        UseMutationOptions<
          Response<'trackUIEvent'>,
          unknown,
          Parameters<Requests['trackUIEvent']>[0],
          unknown
        >,
        'mutationFn'
      >,
    ) =>
      useRapiniMutation<
        Response<'trackUIEvent'>,
        unknown,
        Parameters<Requests['trackUIEvent']>[0]
      >(
        (payload) => requests.trackUIEvent(payload),
        config?.useTrackUIEvent,
        options,
      ),
  } as const;
}
